import React from "react";
import { MessageSquare, CalendarClock, UserPlus, ArrowUpRight, Check } from "lucide-react";
import { useI18n } from "../i18n";

const ICONS = [MessageSquare, CalendarClock, UserPlus];

const SERVICE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/20fe0d92-bbc7-4a3e-a773-b89883ef73fe/images/a94baea5670b60830a5d63b3c41faf2f74a9230ed3e81d8b215d83e70be4068c.png";

const Services = () => {
  const { t } = useI18n();
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600 mb-4">
            {t.services.eyebrow}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
            {t.services.title}
          </h2>
          <p className="text-base md:text-lg text-slate-600">{t.services.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {t.services.items.map((item, i) => {
            const Icon = ICONS[i];
            const isFeatured = i === 1;
            return (
              <article
                key={item.tag}
                data-testid={`service-card-${i}`}
                className={`group bg-white border border-slate-200 rounded-2xl p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 ${
                  isFeatured ? "md:col-span-6 lg:col-span-6 xl:col-span-6 md:p-12" : "md:col-span-3"
                } md:col-span-3 flex flex-col`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-lg bg-slate-900 text-white grid place-items-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-mono text-slate-400">{item.tag}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900">
                  {item.name}
                </h3>
                <p className="mt-3 text-sky-700 font-medium">{item.short}</p>
                <p className="mt-4 text-slate-600 leading-relaxed">{item.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-8 inline-flex items-center gap-1 text-sm font-medium text-slate-900 group-hover:text-sky-600 transition-colors self-start"
                >
                  <span>{t.nav.bookDemo}</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6 items-center bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-8 md:p-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600 mb-3">
              {t.services.items[1].tag} · {t.services.items[1].name}
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900">
              {t.services.items[1].short}
            </h3>
            <p className="mt-4 text-slate-600">{t.services.items[1].desc}</p>
          </div>
          <div className="bg-slate-50 h-full p-6 md:p-10 flex items-center justify-center">
            <img src={SERVICE_IMG} alt="Calendar automation" className="max-h-72 object-contain" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
